import { useRef } from 'react';
import { Download, Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  downloadImeiMacTemplate,
  parseImeiMacFile,
  type ImeiMacRow,
} from '@/utils/imeiExcel';

type Props = {
  serials: ImeiMacRow[];
  quantity: number;
  onChange: (serials: ImeiMacRow[], quantity?: number) => void;
  note?: string;
  className?: string;
};

/**
 * Shared IMEI / MAC editor with Excel template download + upload.
 * Upload replaces the list and sets quantity to the number of rows.
 */
export default function ImeiSerialListEditor({
  serials,
  quantity,
  onChange,
  note,
  className = '',
}: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const qty = Math.max(0, Math.floor(quantity || 0));

  const updateCell = (idx: number, field: keyof ImeiMacRow, value: string) => {
    const next = serials.map((row, i) => (i === idx ? { ...row, [field]: value } : row));
    onChange(next);
  };

  const handleUpload = async (file: File) => {
    try {
      const rows = await parseImeiMacFile(file);
      onChange(rows, rows.length);
      toast.success(`Loaded ${rows.length} IMEI(s) — qty set to ${rows.length}`);
    } catch (err: unknown) {
      toast.error((err as Error)?.message || 'Failed to read IMEI file');
    }
  };

  return (
    <div className={`rounded-xl border border-gray-200 bg-white ${className}`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 border-b border-gray-100 bg-gray-50/80 rounded-t-xl">
        <div>
          <p className="text-sm font-medium text-gray-900">
            IMEI / Serial list <span className="text-red-500">*</span>
            <span className="ml-2 text-xs font-normal text-gray-500">
              {serials.length} of {qty}
            </span>
          </p>
          {note ? <p className="text-xs text-amber-700 mt-0.5">{note}</p> : null}
          <p className="text-xs text-gray-500 mt-0.5">
            Excel columns: <span className="font-medium">IMEI</span> (required),{' '}
            <span className="font-medium">MAC</span> (optional)
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => downloadImeiMacTemplate()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <Download className="w-3.5 h-3.5" />
            Template
          </button>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
          >
            <Upload className="w-3.5 h-3.5" />
            Upload Excel
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) void handleUpload(f);
            }}
          />
        </div>
      </div>

      <div className="p-3 sm:p-4">
        {serials.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-6">
            Set quantity or upload an Excel file to add IMEI rows.
          </p>
        ) : (
          <div className="max-h-64 overflow-y-auto space-y-2 pr-1">
            <div className="hidden sm:grid grid-cols-[2rem_1fr_1fr] gap-2 px-1 text-[11px] font-medium uppercase tracking-wide text-gray-400">
              <span>#</span>
              <span>IMEI</span>
              <span>MAC (optional)</span>
            </div>
            {serials.map((row, idx) => (
              <div
                key={idx}
                className="grid grid-cols-1 sm:grid-cols-[2rem_1fr_1fr] gap-2 items-center"
              >
                <span className="hidden sm:flex text-xs text-gray-400 font-mono justify-center">
                  {idx + 1}
                </span>
                <input
                  required
                  className="input-field font-mono text-sm"
                  placeholder={`IMEI #${idx + 1}`}
                  value={row.imei}
                  onChange={(e) => updateCell(idx, 'imei', e.target.value)}
                />
                <input
                  className="input-field font-mono text-sm"
                  placeholder="MAC (optional)"
                  value={row.mac_address}
                  onChange={(e) => updateCell(idx, 'mac_address', e.target.value)}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
